import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import createCarbonApi from 'api/carbonOffset';
import createOrderApi from 'api/order';
import { currentUserSelector } from 'modules/user/userSlice';
import { AppThunk } from 'store';
import { syncAttribution } from 'modules/user/attributionSlice';
import { appContextSelector } from 'modules/app/appSlice';
import { defaultPortfolioSelector } from 'modules/projects/projectsSlice';
import { setTag, tags } from 'utils/tags';

export type Steps = 'form' | 'customInputs' | 'summary' | 'error';

interface FirstStepData {
  amount?: number;
}

interface SecondStepData {
  orderName?: string;
  country?: Option;
  department?: string;
}

interface OffsetPrice {
  price: number;
  currency: string;
  vat?: number;
}

interface OrderPayload {
  number: string;
  date: string;
  name?: string;
}

interface State {
  amount?: number;
  vat?: number;
  orderName?: string;
  country?: Option;
  department?: string;
  isLoading: boolean;
  price?: number;
  currency?: string;
  step: Steps;
  orderNumber?: string;
  orderDate?: string;
  selectedPortfolioId?: string;
}

const initialState: State = {
  isLoading: false,
  step: 'form',
};

const slice = createSlice({
  name: 'simpleOffsetCarbon',
  initialState,
  reducers: {
    setFirstStep: (state: State, action: PayloadAction<FirstStepData>): State => {
      return { ...state, ...action.payload };
    },
    setSecondStep: (state: State, action: PayloadAction<SecondStepData>): State => {
      return { ...state, ...action.payload };
    },
    reset: (): State => {
      return { ...initialState };
    },
    resetWithoutStep: (state: State): State => {
      return { ...initialState, step: state.step };
    },
    setPrice: (state: State, action: PayloadAction<OffsetPrice>): State => {
      state.price = action.payload.price;
      state.currency = action.payload.currency;
      state.vat = action.payload.vat;
      return state;
    },
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.isLoading = action.payload;
      return state;
    },
    setStep: (state: State, action: PayloadAction<Steps>): State => {
      state.step = action.payload;
      return state;
    },
    setOrder: (state: State, action: PayloadAction<OrderPayload>): State => {
      state.orderNumber = action.payload.number;
      state.orderDate = action.payload.date;
      state.orderName = action.payload.name ?? '';
      return state;
    },
  },
});

const { setFirstStep, setSecondStep, setPrice, reset, setLoading, setStep, setOrder, resetWithoutStep } = slice.actions;
export { setFirstStep, setSecondStep, reset, setStep };
export default slice.reducer;

export const getOffsetPrice = (kilosCo2: number): AppThunk => async (dispatch, getState): Promise<void> => {
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const defaultPortfolio = defaultPortfolioSelector(getState());
  dispatch(setLoading(true));
  try {
    const data = await carbonApi.getCarbonOffsetPrice(
      { kilosCo2, bulkCo2: true, percentageBulkCo2: true },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id ?? '',
    );
    dispatch(
      setPrice({
        price: data.salesPrice,
        currency: data.salesCurrency,
      }),
    );
    dispatch(setLoading(false));
    dispatch(setStep('customInputs'));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setStep('error'));
    dispatch(resetWithoutStep());
  }
};

export const createOrder = (mode: 'quick' | 'full', cb: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);
  const tonesFactor = 1000;
  const { simpleCarbonOffset: carbonOffset, user } = getState();
  const totalKilosCo2 = Number(carbonOffset.amount ?? 0) * tonesFactor;
  const defaultPortfolio = defaultPortfolioSelector(getState());

  const customMetadata: {
    internalName: string;
    value: string;
  }[] = [];

  if (carbonOffset.country) {
    customMetadata.push({
      internalName: 'country',
      value: carbonOffset.country.value,
    });
  }

  if (carbonOffset.department) {
    customMetadata.push({
      internalName: 'department',
      value: carbonOffset.department,
    });
  }

  try {
    const orderResult = await orderApi.create(
      {
        currency: carbonOffset.currency ?? '',
        description: carbonOffset.orderName ?? '',
        comment: '',
        category: 'Carbon',
        items: [
          {
            type: 'impact',
            impact: 'Co2',
            kilosCo2: totalKilosCo2,
            price: carbonOffset.price ?? 0,
            portfolioId: defaultPortfolio?.portfolioData.id ?? 'defaultco2',
            feeFactors: {
              bulkCo2: true,
              percentageBulkCo2: true,
            },
          },
        ],
        payment: {
          type: 'invoice',
        },
        metadata: {
          order: {
            channel: mode === 'quick' ? 'portal-quickoffset' : 'portal-fulloffset',
          },
          custom: customMetadata,
        },
      },
      user.currentAccount?.customerId ?? '',
    );
    setTag({
      ...(mode === 'quick' ? tags.portalQuickOffset.placeOrder : tags.portalOffset.placeOrder),
      categories: [orderResult.order.category],
      transactionId: orderResult.orderId,
      transactionTotal: orderResult.order.totalPrice,
      currencyCode: orderResult.order.currency,
      transactionProducts: orderResult.order.items.map(item => ({
        category: orderResult.order.category,
        name: 'Bulk offset',
        price: item.price,
        quantity: item.kilosCo2,
        sku: 'bulk-offset',
      })),
    });
    dispatch(setLoading(false));
    dispatch(
      setOrder({
        number: orderResult.order.choooseId,
        date: orderResult.order.createdDate,
        name: carbonOffset.orderName ?? '',
      }),
    );
    dispatch(syncAttribution());
    cb();
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setStep('error'));
    dispatch(resetWithoutStep());
  }
};
